import type { Theme, SxProps } from '@mui/material/styles';
import type { ButtonBaseProps } from '@mui/material/ButtonBase';
import { alpha } from '@mui/material/styles';

import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import ButtonBase from '@mui/material/ButtonBase';

import { Iconify } from '../../iconify';
import { svgColorClasses } from '../../svg-color';
import { stylesMode } from '../../theme/styles';

// ----------------------------------------------------------------------

type Props = {
  title: string;
  tooltip?: string;
  sx?: SxProps<Theme>;
  children: React.ReactNode;
};

export function Block({ title, tooltip, children, sx }: Props) {
  return (
    <Box
      sx={{
        px: 2,
        pb: 2,
        pt: 4,
        borderRadius: 2,
        display: 'flex',
        position: 'relative',
        flexDirection: 'column',
        bgcolor: (theme) => alpha(theme.palette.background.paper, 0.6),
        border: (theme) => `solid 1px ${alpha(theme.palette.divider, 0.48)}`,
        boxShadow: (theme) =>
          `0 1px 3px ${alpha(theme.palette.grey[500], 0.08)}`,
        ...sx,
      }}
    >
      <Box
        component="span"
        sx={{
          px: 1.25,
          top: -12,
          fontSize: 13,
          borderRadius: 22,
          lineHeight: '22px',
          position: 'absolute',
          alignItems: 'center',
          color: 'common.white',
          display: 'inline-flex',
          bgcolor: 'text.primary',
          fontWeight: 'fontWeightSemiBold',
          [stylesMode.dark]: { color: 'grey.800' },
        }}
      >
        {title}

        {tooltip && (
          <Tooltip title={tooltip} placement="right">
            <Iconify
              width={14}
              icon="eva:info-outline"
              sx={{ ml: 0.5, mr: -0.5, opacity: 0.48, cursor: 'pointer' }}
            />
          </Tooltip>
        )}
      </Box>

      {children}
    </Box>
  );
}

// ----------------------------------------------------------------------

type BlockOptionProps = ButtonBaseProps & {
  selected?: boolean;
  icon?: React.ReactNode;
  label?: React.ReactNode;
};

export function BlockOption({ icon, label, selected, sx, ...other }: BlockOptionProps) {
  return (
    <ButtonBase
      disableRipple
      sx={{
        width: 1,
        borderRadius: 1.5,
        lineHeight: '18px',
        color: 'text.disabled',
        border: 'solid 1px transparent',
        fontWeight: 'fontWeightSemiBold',
        fontSize: (theme) => theme.typography.pxToRem(13),
        ...(selected && {
          color: 'text.primary',
          bgcolor: 'transparent',
          borderColor: 'primary.main',
          borderWidth: 1.5,
          '&:hover': { bgcolor: 'transparent' },
          '&:focus': { bgcolor: 'transparent' },
          '&:active': { bgcolor: 'transparent' },
          [`& .${svgColorClasses.root}`]: {
            background: 'transparent',
            border: (theme) => `2px solid ${theme.palette.primary.main}`,
          },
        }),
        ...sx,
      }}
      {...other}
    >
      {icon && icon}
      {label && label}
    </ButtonBase>
  );
}
